// Yup validation schemas for the survey builder. The launch schema is the
// static launchValidationSchema (P9): runtime deps (surveyRecipients,
// pausedValidation) travel through Yup context, never a closure.

import * as Yup from "yup";
import dayjs from "dayjs";
import { RESPONSE_OPTION_MAX_LENGTH } from "utils/consts";

const answerTextValidation = Yup.string().max(
    RESPONSE_OPTION_MAX_LENGTH,
    `Response option cannot exceed ${RESPONSE_OPTION_MAX_LENGTH} characters`,
);

const answerSchema = Yup.object({
    text: answerTextValidation.notRequired(),
    position: Yup.number().required(),
});

const answerLaunchSchema = Yup.object({
    text: answerTextValidation.required(),
    position: Yup.number().required(),
});

const QuestionType = Yup.object({
    answer_type: Yup.string().required(),
    description: Yup.string().notRequired(),
    is_allow_custom: Yup.boolean().required(),
    is_random_order: Yup.boolean().required(),
    is_view_all: Yup.boolean().required(),
    position: Yup.number().required(),
    question_type: Yup.string().required(),
    text: Yup.string().notRequired(),
    preset_answers: Yup.array().of(answerSchema),
    // answer_limit mirrors the settings-dialog affordance: the
    // character-limit input only renders for mixed/freeform questions, so
    // only those types require it. A fixed question may carry a null
    // answer_limit (rows created before this rule, or direct API writes);
    // such rows stay saveable, and the launch schema below still blocks
    // launching them with a visible toast.
    answer_limit: Yup.number()
        .nullable()
        .when("question_type", {
            is: (type) => type !== "fixed",
            then: (schema) =>
                schema
                    .required()
                    .min(1, "Question Position must be at least 1")
                    .max(200),
        }),
});

const QuestionLaunchType = Yup.object().shape({
    answer_type: Yup.string().required(),
    description: Yup.string().notRequired(),
    is_allow_custom: Yup.boolean().required(),
    is_random_order: Yup.boolean().required(),
    is_view_all: Yup.boolean().required(),
    position: Yup.number().required(),
    question_type: Yup.string().required(),
    text: Yup.string().required("Each Question needs text!"),
    preset_answers: Yup.array()
        .of(answerLaunchSchema)
        .when("question_type", {
            is: (type) => type !== "mixed" && type !== "freeform",
            then: (Schema) =>
                Schema.test(
                    "if-preset-min-2-asnwers",
                    "Preset answer type questions require at least 2 responses",
                    (value) => value && value.length >= 2,
                ),
        }),
    answer_limit: Yup.number()
        .min(1, "Question Position must be at least 1")
        .max(200),
});

// yupResolver validates the raw values, where a top-level "" stays ""
// rather than becoming undefined, so the optional date tests must
// tolerate an empty string themselves (`if (!value) return true`) and an
// unset date passes validation.
// The launch schema is NOT given this guard: it is called imperatively on
// raw values today and must keep rejecting empty dates at launch time.
export const validationSchemaSurvey = Yup.object({
    name: Yup.string().required("Survey Name Required"),
    description: Yup.string().notRequired(),
    start_date: Yup.string()
        .notRequired()
        .test(
            "Start Datetime",
            "Invalid Start Time",
            (value, testContext) => {
                if (!value) {
                    return true;
                }
                if (!dayjs(value).isValid()) {
                    return testContext.createError({
                        message: "Please enter a valid Start Time!",
                    });
                } else if (dayjs(value).isBefore(dayjs(), "day")) {
                    return testContext.createError({
                        message: "Start Time cannot be in the Past!",
                    });
                } else {
                    return true;
                }
            },
        ),
    end_date: Yup.string()
        .notRequired()
        .test("End Datetime", "Invalid End Time", (value, testContext) => {
            if (!value) {
                return true;
            }
            if (!dayjs(value).isValid()) {
                return testContext.createError({
                    message: "Please enter a valid End Time!",
                });
            } else if (dayjs(value).isBefore(dayjs(), "day")) {
                return testContext.createError({
                    message: "End Time cannot be in the Past!",
                });
            } else {
                return true;
            }
        }),
    start_text: Yup.string().notRequired(),
    end_text: Yup.string().notRequired(),
    questions: Yup.array().of(QuestionType),
});

// Static launch schema (P9). One module-level schema; the runtime
// dependencies the old factory closed over (surveyRecipients) plus the
// paused flag are read from Yup's validation context inside each .test()
// at validation time:
//
//   await launchValidationSchema.validate(values, {
//       context: {
//           surveyRecipients,
//           pausedValidation: survey.state === "paused",
//       },
//   });
//
// Rules and messages are byte-identical to the factory they replaced.
export const launchValidationSchema = Yup.object({
    name: Yup.string().required("Survey Name Required"),
    description: Yup.string().notRequired(),
    start_date: Yup.string()
        .notRequired()
        .test(
            "Start Datetime",
            "Invalid Start Time",
            (value, testContext) => {
                const { pausedValidation } =
                    testContext.options.context || {};
                if (!dayjs(value).isValid()) {
                    return testContext.createError({
                        message: "Please enter a valid Start Time!",
                    });
                } else if (
                    !pausedValidation &&
                    dayjs(value).isBefore(dayjs(), "day")
                ) {
                    return testContext.createError({
                        message: "Start Time cannot be in the Past!",
                    });
                } else {
                    return true;
                }
            },
        ),
    is_allow_anonymous: Yup.boolean().test(
        "Participants",
        "No Valid Participants",
        (value, testContext) => {
            const { surveyRecipients } = testContext.options.context || {};
            if (!value && (!surveyRecipients || surveyRecipients.length < 1)) {
                return testContext.createError({
                    message:
                        "Either allow Anonymous Participants or Add Them!",
                });
            } else {
                return true;
            }
        },
    ),
    end_date: Yup.string()
        .required("Please ensure the survey has a valid end date")
        .test("End Datetime", "Invalid End Time", (value, testContext) => {
            const { pausedValidation } =
                testContext.options.context || {};
            if (!dayjs(value).isValid()) {
                return testContext.createError({
                    message: "Please enter a valid End Time!",
                });
            } else if (
                !pausedValidation &&
                dayjs(value).isBefore(dayjs(), "day")
            ) {
                return testContext.createError({
                    message: "End Time cannot be in the Past!",
                });
            } else {
                return true;
            }
        })
        .test(
            "End Date after Start Date",
            "End Date must be after Start Date!",
            function (value) {
                const { start_date } = this.parent;
                return dayjs(value).isAfter(dayjs(start_date));
            },
        ),
    start_text: Yup.string().required("Start Text Required"),
    end_text: Yup.string().required("End Text Required"),
    questions: Yup.array(QuestionLaunchType).min(1),
});

export const validationSchemaTheme = Yup.object({
    name: Yup.string().required("Theme Name Required"),
    description: Yup.string().notRequired(),
});

export const validationSchemaAnswerThemes = Yup.object({
    themes: Yup.array(),
});

export const validationSchemaThemeAnswers = Yup.object({
    theme: Yup.string(),
    answers_preset: Yup.array(),
    answers_custom: Yup.array(),
});
